import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, FileText, Store, ArrowLeft, CircleAlert } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import CategoryPickerTrigger from './CategoryPicker';
import Button from './Button';
import '../transactions/Transactions.css';
import { Z } from '../../constants/zIndex';
import {
  buildPickerSections,
} from '../../utils/incomeClassification';
import {
  createEmptyManualTransactionForm,
  createManualTransaction,
  isManualTransactionFormPristine,
  todayISO,
  validateManualTransactionForm,
} from '../../services/manualTransactionService';
import { resolveManualTransactionMutationOutcome } from '../../utils/manualTransactionMutationState';
import {
  buildMutationIntentStorageKey,
  clearMutationIntent,
  getOrCreateMutationIntent,
  isIdempotencyContractError,
  shouldRetainMutationIntent,
} from '../../utils/mutationContracts';

export default function AddTransactionModal({ isOpen, onClose, categories }) {
  const reduceMotion = useReducedMotion();
  const { authenticatedFetch, household, user } = useAuth();
  const { pushToast } = useToast();

  const [form, setForm] = useState(createEmptyManualTransactionForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);

  const merchantRef = useRef(null);
  const modalRef = useRef(null);
  const notesRef = useRef(null);
  const createIntentRef = useRef(null);
  const createIntentStorageKey = buildMutationIntentStorageKey({
    operation: 'manual-transaction',
    actorUserId: user?.id,
    householdId: household?.id,
  });

  // Reset and autofocus when modal opens
  useEffect(() => {
    if (isOpen) {
      setForm(createEmptyManualTransactionForm());
      setErrors({});
      setSubmitting(false);
      setNotesExpanded(false);
      const t = setTimeout(() => merchantRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    createIntentRef.current = null;
  }, [household?.id, user?.id]);

  const closeModal = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [onClose, submitting]);

  useFocusTrap(modalRef, isOpen, closeModal);

  const isPristine = isManualTransactionFormPristine(form);

  const handleBackdropClick = () => { if (isPristine) closeModal(); };

  const set = useCallback((field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  }, []);

  const merchantHasValue = form.merchant.trim().length > 0;
  const amountHasValue = String(form.amount ?? '').trim().length > 0;
  const notesHasValue = form.notes.trim().length > 0;
  const dateHasCustomValue = form.date && form.date !== todayISO();
  const categoryHasValue = Boolean(form.categoryId);

  const validate = () => {
    return validateManualTransactionForm(form);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const intent = getOrCreateMutationIntent(createIntentRef, {
        scope: 'manual-transaction',
        payload: form,
        storageKey: createIntentStorageKey,
      });
      const createdTransaction = await createManualTransaction({
        authenticatedFetch,
        householdId: household.id,
        form,
        categories,
        idempotencyKey: intent.key,
      });
      clearMutationIntent(createIntentRef, createIntentStorageKey);
      const mutationOutcome = resolveManualTransactionMutationOutcome({
        success: true,
        merchant: createdTransaction.merchant,
        amount: createdTransaction.amount,
      });

      pushToast(mutationOutcome.toast);

      if (mutationOutcome.shouldDispatchCreatedEvent) {
        window.dispatchEvent(new CustomEvent('vylo:transaction-created', {
          detail: { transaction: createdTransaction }
        }));
      }
      onClose();
    } catch (err) {
      if (!shouldRetainMutationIntent(err)) {
        clearMutationIntent(createIntentRef, createIntentStorageKey);
      }
      const mutationOutcome = resolveManualTransactionMutationOutcome({
        success: false,
        errorMessage: isIdempotencyContractError(err)
          ? 'Vylo is reconciling this transaction. Your draft is still here.'
          : err.message,
      });
      pushToast(mutationOutcome.toast);
    } finally {
      setSubmitting(false);
    }
  };

  const modal = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            className="fixed inset-0"
            style={{ zIndex: Z.ADD_TRANSACTION_MODAL - 1, backgroundColor: 'rgba(0,0,0,0.6)' }}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.15 }}
            onClick={handleBackdropClick}
            aria-hidden="true"
          />

          {/* Modal */}
          <motion.div
            key="modal"
            className="fixed inset-0 flex items-center justify-center p-6 pointer-events-none"
            style={{ zIndex: Z.ADD_TRANSACTION_MODAL }}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.97, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: 6 }}
            transition={{ duration: reduceMotion ? 0 : 0.18, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <div
              ref={modalRef}
              role="dialog"
              aria-modal="true"
              aria-label="Add transaction"
              tabIndex={-1}
              className="workflow-transaction-composer pointer-events-auto relative h-[min(650px,calc(100vh-5rem))] w-full max-w-[600px] overflow-hidden rounded-[18px] bg-[var(--color-surface)] shadow-xl"
            >
              <div className="h-full overflow-hidden">
                <form className="h-full" onSubmit={handleSubmit} noValidate>
                  <div className="flex h-full min-h-0 flex-col">
                  <div className="workflow-composer-content min-h-0 flex-1 overflow-y-auto px-8 pb-8 pt-7">
                    <div className="flex items-start justify-between gap-6">
                      <h2 className="workflow-composer-title">New Transaction</h2>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={closeModal}
                        disabled={submitting}
                        aria-label="Close"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[var(--color-text-secondary)] transition-colors hover:bg-black/5 hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring-strong)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[var(--color-text-secondary)]"
                      >
                        <X className="h-6 w-6" strokeWidth={1.75} />
                      </Button>
                    </div>

                    <div className="workflow-composer-fields">
                      <div className="workflow-composer-merchant flex items-start gap-4">
                        <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-row)] text-[var(--color-text-muted)]">
                          <Store className="h-6 w-6" strokeWidth={1.8} />
                        </div>

                        <div className="min-w-0 flex-1">
                          <input
                            ref={merchantRef}
                            aria-label="Merchant Name"
                            type="text"
                            value={form.merchant}
                            onChange={(e) => set('merchant', e.target.value)}
                            placeholder="Merchant Name"
                            autoComplete="off"
                            className={`workflow-composer-merchant-input w-full font-semibold focus:outline-none ${
                              errors.merchant
                                ? 'text-[var(--color-danger)] placeholder:text-red-300'
                                : merchantHasValue
                                  ? 'text-[var(--color-text-primary)]'
                                  : 'text-[var(--color-text-muted)] placeholder:text-[var(--color-text-muted)]'
                            }`}
                          />

                          <div className="mt-4 flex flex-wrap items-center gap-3 text-base">
                            <label className={`inline-flex items-center gap-2 ${errors.date ? 'text-[var(--color-danger)]' : dateHasCustomValue ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-muted)]'}`}>
                              <input
                                type="date"
                                aria-label="Date"
                                value={form.date}
                                onChange={(e) => set('date', e.target.value)}
                                className="workflow-composer-date bg-transparent text-sm font-medium focus:outline-none [color-scheme:inherit]"
                              />
                            </label>
                          </div>

                          {errors.merchant && (
                            <p className="mt-3 text-sm text-[var(--color-danger)]">{errors.merchant}</p>
                          )}
                          {errors.date && (
                            <p className="mt-2 text-sm text-[var(--color-danger)]">{errors.date}</p>
                          )}
                        </div>
                      </div>

                      <div className="mb-5">
                        <div className="workflow-composer-amount flex items-baseline gap-1.5">
                          <span
                            className={`text-4xl font-semibold tracking-tight [font-variant-numeric:tabular-nums] ${
                              errors.amount
                                ? 'text-[var(--color-danger)]'
                                : amountHasValue
                                  ? 'text-[var(--color-text-primary)]'
                                  : 'text-[var(--color-text-muted)]'
                            }`}
                          >
                            $
                          </span>
                          <input
                            type="number"
                            aria-label="Amount"
                            inputMode="decimal"
                            step="0.01"
                            value={form.amount}
                            onChange={(e) => set('amount', e.target.value)}
                            placeholder="0.00"
                            className={`w-full border-0 bg-transparent px-0 py-0 text-4xl font-semibold tracking-tight [font-variant-numeric:tabular-nums] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus:outline-none ${
                              errors.amount
                                ? 'text-[var(--color-danger)] placeholder:text-red-300'
                                : amountHasValue
                                  ? 'text-[var(--color-text-primary)]'
                                  : 'text-[var(--color-text-muted)] placeholder:text-[var(--color-text-muted)]'
                            }`}
                          />
                        </div>
                        {errors.amount && (
                          <p className="mt-3 text-sm text-[var(--color-danger)]">{errors.amount}</p>
                        )}
                      </div>

                      <div className="mb-5">
                        {notesExpanded || form.notes ? (
                          <div className="rounded-xl">
                            <input
                              ref={notesRef}
                              value={form.notes}
                              onChange={(e) => set('notes', e.target.value)}
                              onBlur={() => {
                                if (!form.notes.trim()) setNotesExpanded(false);
                              }}
                              placeholder="Add note..."
                              className={`w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-input)] px-3 py-2.5 text-base focus:outline-none ${
                                notesHasValue
                                  ? 'text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]'
                                  : 'text-[var(--color-text-muted)] placeholder:text-[var(--color-text-muted)]'
                              }`}
                            />
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="unstyled"
                            size="none"
                            onClick={() => {
                              setNotesExpanded(true);
                              requestAnimationFrame(() => notesRef.current?.focus());
                            }}
                            className="workflow-composer-note flex w-full items-center justify-start gap-2.5 rounded-xl px-3 py-2.5 text-left text-base text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-surface-input)]"
                          >
                            <FileText className="h-4 w-4" strokeWidth={1.9} />
                            <span>Add note...</span>
                          </Button>
                        )}
                      </div>

                      <div className="relative mt-3">
                        <div className={`pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 ${categoryHasValue ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'}`}>
                          <CircleAlert className="h-5 w-5 shrink-0" strokeWidth={1.8} />
                        </div>
                          <CategoryPickerTrigger
                          categories={categories}
                          sections={buildPickerSections({
                            categories,
                            includeIncome: true,
                          })}
                          value={form.categoryId}
                          onChange={(id) => set('categoryId', id)}
                          placeholder="Assign Category"
                          showChevron
                          pickerZIndex={Z.ADD_TRANSACTION_MODAL + 10}
                          trapFocus
                          allowCreate
                          householdId={household.id}
                          actorUserId={user?.id}
                          authenticatedFetch={authenticatedFetch}
                          buttonClassName={`!h-[52px] !w-full !rounded-xl !border !bg-[var(--color-surface-input)] !pl-11 !pr-4 !text-left !text-base !font-medium !shadow-none ${
                            categoryHasValue
                              ? '!border-[var(--color-border)] !text-[var(--color-text-primary)] hover:!bg-[var(--color-surface-row)] hover:!border-[var(--color-border-strong)]'
                              : '!border-[var(--color-border)] !text-[var(--color-text-muted)] hover:!bg-[var(--color-surface-row)] hover:!border-[var(--color-border-strong)]'
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="workflow-composer-footer mt-auto flex h-[88px] flex-shrink-0 items-center border-t border-[var(--color-border)] bg-[var(--color-surface-row)] px-8 shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]">
                    <div className="flex w-full items-center justify-between gap-3">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={closeModal}
                      disabled={submitting}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-row)] disabled:opacity-50"
                    >
                      <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      loading={submitting}
                      loadingLabel="Saving transaction…"
                      className="h-10 px-4 text-sm"
                    >
                      Add Transaction
                    </Button>
                    </div>
                  </div>
                  </div>
                </form>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modal, document.body);
}
